import { FC, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { useQuery } from 'react-query'
import { OptionsHelper } from '_metronic/helpers'
import { showConfirmAlert } from '_metronic/helpers/AlertHelper'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  FetchState,
  FormSelect,
  FormTextArea,
  IOption,
  ListLoading,
  useModal,
  useToast,
} from 'app/components'
import { ErroDeCarga } from '../../dispositivos/core/ErroDeCarga'
import { RegimeDispositivo } from '../../dispositivos/core/_models'
import { opcao, opcoesRegime, rotuloRegime } from '../../dispositivos/core/rotulos'
import { textoOuNulo } from '../../dispositivos/core/textoOuNulo'
import { trataErroDoModal } from '../../dispositivos/core/errosFormulario'
import { TelekitView } from '../telekits-list/core/_models'
import { getAlocacoesTelekit, transferirCustodiaTelekit } from '../telekits-list/core/_requests'
import { ErrosGerais } from '../../dispositivos/core/ErrosGerais'
import { RodapeModal } from '../../dispositivos/core/RodapeModal'

type Valores = {
  empresaDestino?: IOption<number>
  regime?: IOption<RegimeDispositivo>
  observacao: string
}

const CAMPOS = ['empresaDestino', 'regime', 'observacao'] as const
const MAPA = { idEmpresaDestino: 'empresaDestino' }

/** Troca a empresa e/ou o regime da maleta. Espera o fetcher `telekit` no `showModal`. */
export const TransferirCustodiaModal: FC = () => {
  const { fetchStates } = useModal()
  const {
    data: telekit,
    isLoading,
    error,
  } = (fetchStates['telekit'] || {}) as FetchState<TelekitView>
  if (error) return <ErroDeCarga error={error} />
  // O formulário só monta com a maleta carregada: os valores iniciais dependem do regime atual.
  if (isLoading || !telekit) return <ListLoading />
  return <FormularioCustodia telekit={telekit} />
}

const FormularioCustodia: FC<{ telekit: TelekitView }> = ({ telekit }) => {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const { t } = useT()
  const [erroGeral, setErroGeral] = useState<string[]>([])
  const empresaAtual = telekit?.empresa?.id
  const regimeAtual: RegimeDispositivo = telekit?.regime ?? 'LAUDUZ'
  const padrao = !!telekit?.padrao
  const vendida = regimeAtual === 'VENDIDO'

  const { data: ativas } = useQuery(
    ['telekit-alocacoes-ativas-total', telekit?.id],
    () => getAlocacoesTelekit(telekit?.id as number, 'ativo=true&page=0&size=1'),
    { enabled: !!telekit.id, refetchOnWindowFocus: false }
  )
  const quantidadeAtivas = ativas?.totalElements ?? 0
  const opcoesRegimes = useMemo(() => opcoesRegime(t), [t])

  // Ao menos um dos dois muda (mesma regra e frase do backend).
  const mudancas = (valores: Valores) => ({
    trocaEmpresa: !!valores.empresaDestino && valores.empresaDestino.value !== empresaAtual,
    trocaRegime: !!valores.regime && valores.regime.value !== regimeAtual,
  })

  const formik = useFormik<Valores>({
    initialValues: {
      empresaDestino: undefined,
      regime: opcao(regimeAtual, (v) => rotuloRegime(t, v)),
      observacao: '',
    },
    validate: (valores) => {
      const { trocaEmpresa, trocaRegime } = mudancas(valores)
      return trocaEmpresa || trocaRegime
        ? {}
        : { empresaDestino: t('pagina.telekits.custodia.sem_alteracao') }
    },
    onSubmit: async (valores, helpers) => {
      if (!telekit.id) return
      setErroGeral([])
      const { trocaEmpresa, trocaRegime } = mudancas(valores)
      const destino = trocaEmpresa
        ? valores.empresaDestino?.label
        : telekit.empresa?.nomeFantasia ?? ''

      const confirmado = await showConfirmAlert({
        title: t('pagina.telekits.custodia.confirmar_titulo'),
        text: t('pagina.telekits.custodia.confirmar_texto', {
          quantidade: quantidadeAtivas,
          destino,
        }),
        confirmButtonText: t('pagina.telekits.custodia.confirmar'),
        cancelButtonText: t('common.cancel'),
        danger: false,
      })
      if (!confirmado) return

      try {
        await transferirCustodiaTelekit(telekit.id, {
          idEmpresaDestino: trocaEmpresa ? valores.empresaDestino?.value ?? null : null,
          regime: trocaRegime ? valores.regime?.value ?? null : null,
          observacao: textoOuNulo(valores.observacao),
        })
        showToast({ variant: 'success', title: t('pagina.telekits.custodia.sucesso') })
        closeModal()
      } catch (error) {
        // Na Padrão o regime não tem campo: o erro dele vai para a caixa do topo.
        const campos = padrao ? CAMPOS.filter((c) => c !== 'regime') : CAMPOS
        trataErroDoModal(error, helpers, campos, setErroGeral, MAPA)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <ErrosGerais erros={erroGeral} />
      <p className='text-gray-700 mb-6'>
        {t('pagina.telekits.custodia.atual', {
          empresa: telekit.empresa?.nomeFantasia ?? '—',
          regime: rotuloRegime(t, regimeAtual),
        })}
      </p>

      <div className='mb-5'>
        <FormSelect
          formik={formik}
          name='empresaDestino'
          label={t('pagina.telekits.custodia.empresa_destino')}
          inputId='custodia-empresa'
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          fetchOptionsParams={{ onlyActive: false }}
          isClearable
          isDisabled={vendida}
        />
        {vendida && (
          <div className='form-text text-gray-700'>
            {t('pagina.telekits.custodia.motivo_vendida')}
          </div>
        )}
      </div>

      <div className='mb-5'>
        {padrao ? (
          <>
            <div className='fs-7 fw-bolder mb-2'>{t('pagina.dispositivos.campo.regime')}</div>
            <div className='text-gray-900'>{rotuloRegime(t, 'LAUDUZ')}</div>
            <div className='form-text text-gray-700'>
              {t('pagina.telekits.custodia.motivo_padrao')}
            </div>
          </>
        ) : (
          <FormSelect
            formik={formik}
            name='regime'
            label={t('pagina.dispositivos.campo.regime')}
            inputId='custodia-regime'
            options={opcoesRegimes}
            isClearable={false}
          />
        )}
      </div>

      <FormTextArea
        formik={formik}
        name='observacao'
        label={t('pagina.telekits.dispositivos.campo.observacao')}
        rows={3}
      />

      <RodapeModal
        enviando={formik.isSubmitting}
        rotuloEnviar={t('pagina.telekits.custodia.acao')}
      />
    </form>
  )
}
